import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface ProfileReadiness {
  hasType: boolean;
  isComplete: boolean;
  loading: boolean;
}

export const useProfileReady = (): ProfileReadiness => {
  const { profile, loading: authLoading } = useAuth();

  const { data: caregiverDetails, isLoading: caregiverLoading } = useQuery({
    queryKey: ["caregiver-readiness", profile?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("caregiver_details")
        .select("id")
        .eq("profile_id", profile!.id)
        .maybeSingle();

      if (!data) return { hasDetails: false, hasServices: false };

      const { count } = await supabase
        .from("caregiver_services")
        .select("id", { count: "exact", head: true })
        .eq("caregiver_id", data.id)
        .eq("is_enabled", true);

      return { hasDetails: true, hasServices: (count ?? 0) > 0 };
    },
    enabled: !!profile && profile.user_type === "compawdre",
  });

  if (authLoading) {
    return { hasType: false, isComplete: false, loading: true };
  }

  if (!profile) {
    return { hasType: false, isComplete: false, loading: false };
  }

  const hasType = profile.user_type !== null;

  if (!hasType) {
    return { hasType: false, isComplete: false, loading: false };
  }

  if (profile.user_type === "humano") {
    const isComplete = !!(
      profile.full_name &&
      profile.phone &&
      (profile as any).date_of_birth
    );
    return { hasType: true, isComplete, loading: false };
  }

  // Compawdre
  if (caregiverLoading) {
    return { hasType: true, isComplete: false, loading: true };
  }

  const isComplete = !!(
    profile.full_name &&
    profile.phone &&
    (profile as any).date_of_birth &&
    profile.bio &&
    profile.address_city &&
    caregiverDetails?.hasDetails &&
    caregiverDetails?.hasServices
  );

  return { hasType: true, isComplete, loading: false };
};
